import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  FilePdf, 
  FileDoc, 
  FileXls, 
  FileText,
  MagnifyingGlass,
  CircleNotch,
  CheckCircle,
  XCircle,
  Warning,
  Lightbulb
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [id]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}`);
      setDocument(response.data);
    } catch (error) {
      toast.error("Document non trouvé");
      navigate("/documents");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/documents/${id}/analyze`);
      toast.success("Analyse terminée");
      setDocument(prev => ({
        ...prev,
        analysis_status: "completed",
        compliance_score: response.data.compliance_score,
        analysis_result: response.data.analysis
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
      setDocument(prev => ({ ...prev, analysis_status: "failed" }));
    } finally {
      setAnalyzing(false);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf": return <FilePdf weight="duotone" className="w-10 h-10 text-red-400" />;
      case "docx": return <FileDoc weight="duotone" className="w-10 h-10 text-blue-400" />;
      case "xlsx": return <FileXls weight="duotone" className="w-10 h-10 text-green-400" />;
      default: return <FileText weight="duotone" className="w-10 h-10 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "conforme": return "text-cyber-success border-cyber-success bg-cyber-success/10";
      case "partiel": return "text-cyber-volt border-cyber-volt bg-cyber-volt/10";
      case "non_conforme": return "text-cyber-danger border-cyber-danger bg-cyber-danger/10";
      default: return "text-slate-400 border-slate-400 bg-slate-400/10";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cyber-primary font-mono">Chargement...</div>
      </div>
    );
  }

  const analysis = document?.analysis_result;

  return (
    <div data-testid="document-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/documents")}
          className="text-slate-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading font-black text-2xl tracking-tight mb-1">
            {document?.name}
          </h1>
          <p className="text-slate-400 text-sm font-mono">
            Uploadé par {document?.uploaded_by_name}
          </p>
        </div>
        {(document?.analysis_status === "pending" || document?.analysis_status === "failed") && (
          <Button
            onClick={handleAnalyze}
            disabled={analyzing}
            className="bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
            data-testid="analyze-btn"
          >
            {analyzing ? (
              <>
                <CircleNotch weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                <MagnifyingGlass weight="duotone" className="w-4 h-4 mr-2" />
                Analyser avec IA
              </>
            )}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Document Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-cyber-bg rounded-sm flex items-center justify-center">
              {getFileIcon(document?.file_type)}
            </div>
            <div>
              <p className="font-mono text-xs text-slate-400 uppercase">{document?.file_type}</p>
              <p className="text-sm text-slate-300">
                {new Date(document?.created_at).toLocaleDateString("fr-FR")}
              </p>
            </div>
          </div>

          {document?.compliance_score !== null && (
            <div className="mb-6">
              <p className="text-sm text-slate-400 mb-2">Score de conformité</p>
              <div className="flex items-end gap-2">
                <span className="font-mono text-4xl text-cyber-primary">
                  {document.compliance_score}
                </span>
                <span className="text-slate-400 text-lg mb-1">%</span>
              </div>
              <div className="progress-cyber mt-2">
                <div 
                  className="progress-cyber-fill" 
                  style={{ width: `${document.compliance_score}%` }}
                />
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Statut</span>
              <span className={`font-mono ${
                document?.analysis_status === "completed" ? "text-cyber-success" :
                document?.analysis_status === "analyzing" ? "text-cyber-volt" :
                document?.analysis_status === "failed" ? "text-cyber-danger" : "text-slate-400"
              }`}>
                {document?.analysis_status === "completed" ? "Analysé" :
                 document?.analysis_status === "analyzing" ? "En cours" :
                 document?.analysis_status === "failed" ? "Échec" : "En attente"}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Analysis Results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="cyber-card p-6 lg:col-span-2"
        >
          <h2 className="font-heading font-bold text-lg mb-4">Résultats de l'analyse</h2>
          
          {analyzing ? (
            <div className="flex flex-col items-center justify-center py-16">
              <CircleNotch weight="bold" className="w-12 h-12 text-cyber-primary animate-spin mb-4" />
              <p className="text-slate-400">Analyse IA en cours...</p>
              <p className="text-xs text-slate-500 mt-1">Cela peut prendre quelques instants</p>
            </div>
          ) : !analysis ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <MagnifyingGlass weight="duotone" className="w-12 h-12 mb-4 opacity-50" />
              <p>Aucune analyse disponible</p>
              <p className="text-sm text-slate-500 mt-1">Cliquez sur "Analyser avec IA" pour démarrer</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              {/* Summary */}
              {analysis.summary && (
                <div className="mb-6 p-4 bg-cyber-bg rounded-sm border border-cyber-border">
                  <h3 className="font-medium text-sm text-cyber-primary mb-2">Résumé</h3>
                  <p className="text-sm text-slate-300">{analysis.summary}</p>
                </div>
              )}

              {/* Strengths & Weaknesses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {analysis.strengths?.length > 0 && (
                  <div className="p-4 bg-cyber-success/5 border border-cyber-success/30 rounded-sm">
                    <h3 className="flex items-center gap-2 font-medium text-sm text-cyber-success mb-3">
                      <CheckCircle weight="fill" className="w-4 h-4" />
                      Points forts
                    </h3>
                    <ul className="space-y-2">
                      {analysis.strengths.map((item, i) => (
                        <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                          <span className="text-cyber-success mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.weaknesses?.length > 0 && (
                  <div className="p-4 bg-cyber-danger/5 border border-cyber-danger/30 rounded-sm">
                    <h3 className="flex items-center gap-2 font-medium text-sm text-cyber-danger mb-3">
                      <Warning weight="fill" className="w-4 h-4" />
                      Points faibles
                    </h3>
                    <ul className="space-y-2">
                      {analysis.weaknesses.map((item, i) => (
                        <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                          <span className="text-cyber-danger mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Recommendations */}
              {analysis.recommendations?.length > 0 && (
                <div className="mb-6 p-4 bg-cyber-volt/5 border border-cyber-volt/30 rounded-sm">
                  <h3 className="flex items-center gap-2 font-medium text-sm text-cyber-volt mb-3">
                    <Lightbulb weight="fill" className="w-4 h-4" />
                    Recommandations
                  </h3>
                  <ul className="space-y-2">
                    {analysis.recommendations.map((item, i) => (
                      <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                        <span className="text-cyber-volt mt-1">{i + 1}.</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Clauses Analysis */}
              {analysis.clauses_analysis?.length > 0 && (
                <div>
                  <h3 className="font-medium text-sm text-slate-400 mb-3">Analyse par clause ISO 27001</h3>
                  <div className="space-y-2">
                    {analysis.clauses_analysis.map((clause, i) => (
                      <div 
                        key={i}
                        className="p-3 bg-cyber-bg rounded-sm border border-cyber-border flex items-start gap-3"
                      >
                        <span className={`badge-clause ${getStatusColor(clause.status)}`}>
                          {clause.clause}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{clause.title}</p>
                          <p className="text-xs text-slate-400 mt-1">{clause.comment}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw response fallback */}
              {analysis.raw_response && (
                <div className="p-4 bg-cyber-bg rounded-sm border border-cyber-border">
                  <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap">
                    {analysis.raw_response}
                  </pre>
                </div>
              )}
            </ScrollArea>
          )}
        </motion.div>
      </div>
    </div>
  );
}
